import { useState, ChangeEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { User } from "../../types/slice.types";

interface FieldErrors {
  name?: string;
  email?: string;
  phone?: string;
  city?: string;
}

interface EditProfileCardProps {
  user: Partial<User>;
  onSubmit: (data: Partial<User>) => void;
  fieldErrors?: FieldErrors;
  setFieldErrors?: (errors: FieldErrors) => void;
  onClose: () => void;
}

export const EditProfileCard = ({
  user,
  onSubmit,
  fieldErrors = {},
  setFieldErrors = () => {},
  onClose,
}: EditProfileCardProps) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<Partial<User>>({
    name: "",
    email: "",
    phone: "",
    city: "",
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        city: user.city || "",
      });
    }

    return () => setFieldErrors({});
  }, [user]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 text-primaryText dark:text-primaryTextDark flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        className="relative bg-popupLight dark:bg-popupDark shadow-lg rounded-2xl p-8 w-full max-w-md mx-4 animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-bold text-center mb-6">
          {t("Edit Profile")}
        </h2>

        <form className="space-y-4">
          <div>
            <label className="block text-sm font-medium">{t("name")}</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="mt-1 w-full px-4 py-2 rounded-md dark:bg-gray-700"
            />
            <span
              className={`text-sm text-red-500 ${
                fieldErrors.name ? "visible" : "invisible"
              }`}
            >
              {fieldErrors.name || "placeholder"}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium">{t("email")}</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="mt-1 w-full px-4 py-2 rounded-md dark:bg-gray-700"
            />
            <span
              className={`text-sm text-red-500 ${
                fieldErrors.email ? "visible" : "invisible"
              }`}
            >
              {fieldErrors.email || "placeholder"}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium">
              {t("phoneNumber")}
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="mt-1 w-full px-4 py-2 rounded-md dark:bg-gray-700"
            />
            <span
              className={`text-sm text-red-500 ${
                fieldErrors.phone ? "visible" : "invisible"
              }`}
            >
              {fieldErrors.phone || "placeholder"}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium">{t("city")}</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className="mt-1 w-full px-4 py-2 rounded-md dark:bg-gray-700"
            />
            <span
              className={`text-sm text-red-500 ${
                fieldErrors.city ? "visible" : "invisible"
              }`}
            >
              {fieldErrors.city || "placeholder"}
            </span>
          </div>

          <button
            type="button"
            onClick={() => onSubmit(formData)}
            className="w-full bg-secondaryText dark:bg-secondaryTextDark text-primaryText py-2 rounded-md"
          >
            {t("Save Changes")}
          </button>
        </form>
      </div>
    </div>
  );
};
